import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, switchMap, tap, mergeMap, of } from 'rxjs';
import * as moment from 'moment';
import { GroupService } from './group.service';
import { PdfDownloadService } from './pdf-download.service';
import { PracticeSettingsStore } from './practice-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class ConsentFormsService {
  private apiUrl = 'api';
  private consentFormsSubject = new BehaviorSubject<any>(null);
  consentForms$ = this.consentFormsSubject.asObservable();
  private consentTemplatesSubject = new BehaviorSubject<any[]>([]);
  consentTemplates$ = this.consentTemplatesSubject.asObservable();
  groupId?: string;
  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private pdfDownloadService: PdfDownloadService,
    private settingsStore?: PracticeSettingsStore,
  ) {
    // Load consent forms when group changes
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          return this.getConsentFormsByGroup(selectedGroup.id, true);
        }
        return EMPTY;
      })
    ).subscribe();

    // Load templates when group changes (separate stream to avoid nested subscriptions)
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup && selectedGroup.groupSpeciality) {
          return this.getConsentTemplatesBySpecialty(selectedGroup.groupSpeciality, true);
        }
        this.consentTemplatesSubject.next([]);
        return EMPTY;
      })
    ).subscribe();
  }

  /** `fromKeptCopy`: on a practice switch, the device's kept copy while it is current (PracticeSettingsStore). */
  getConsentFormsByGroup(groupId: string, fromKeptCopy = false): Observable<any> {
    const token = localStorage.getItem('token');
    const request = this.http.get(`${this.apiUrl}/consent-forms/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(groupId, 'consentForms', () => request) : request;
    return source.pipe(tap(consentForms => { this.consentFormsSubject.next(consentForms); }));
  }

  getConsentTemplatesBySpecialty(specialty: string, fromKeptCopy = false): Observable<any[]> {
    const token = localStorage.getItem('token');
    const request = this.http.get<any[]>(`${this.apiUrl}/consent-templates/by-specialty/${encodeURIComponent(specialty)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(this.groupId!, `consentTemplates:${specialty}`, () => request) : request;
    return source.pipe(
      tap(templates => {
        // Only include active templates
        const activeTemplates = (templates || []).filter((t: any) => t.isActive);
        this.consentTemplatesSubject.next(activeTemplates);
      })
    );
  }

  deleteConsentForm(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/consent-forms/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getConsentFormsByGroup(this.groupId!))
    );
  }

  updateConsentForms(formData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/consent-forms/${this.groupId}`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getConsentFormsByGroup(this.groupId!))
    );
  }

  updateInformationSheet(changes: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/consent-forms/information-sheet/${this.groupId}`, changes, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  buildInteractiveConsentPreview(consent: any): string {

    let consentText = ''
    if (consent.consentType === 'interactive') {
      consentText = `
      <strong>CONSENT FOR ${consent.procedureTitle?.toUpperCase() || '**PROCEDURE TITLE**'}</strong>\n
      `;

      if (consent.condition) {
        consentText += `${consent.condition}\n\n`;
      }

      if (consent.treatment) {
        consentText += `${consent.treatment}\n\n`;
      }

      if (consent.generalRisks) {
        consentText += `${consent.generalRisks}\n\n`;
      }

      if (consent.specificRisks) {
        consentText += `${consent.specificRisks}\n\n`;
      }

    } else {
      consentText = `
      <strong>${consent.consentLabel?.toUpperCase() || consent.label?.toUpperCase()}</strong>\n
      `;
    }

    if (consent.text) {
      consentText += `${consent.text}\n\n`;
    }

    if (consent.confirmSide && consent.sideOptions?.length) {
      consentText += `PROCEDURE SIDE: \n`;
      consentText += '\n\n';
    }


    if (consent.confirmImplant && consent.implantOptions?.length) {
      consentText += `SELECTED IMPLANT: \n`;
      consentText += '\n\n';
    }
    
    
    if (consent.confirmTransfusion && consent.transfusionOptions?.length) {
      consentText += `BLOOD TRANSFUSION:\n`;
      consentText += '\n\n';
    }

    if (consent.additionalRequiredConsent?.length) {
      const additionalRequiredConsent = consent.additionalRequiredConsent;
      consentText += `REQUIRED CONSENT\nI Consent to the following:\n`;
      for (let consent of additionalRequiredConsent) {
        consentText += `[] ${consent}\n`;
      }
      consentText += '\n\n';
    }

    if (consent.conditionalConsent?.length) {
      const conditionalConsent = consent.conditionalConsent;
      consentText += `OPTIONAL CONSENT\nI Consent to the following:\n`;
      for (let consent of conditionalConsent) {
        consentText += `[] ${consent}\n`;
      }
      consentText += '\n\n';
    }

    consentText += consent.consentType === 'interactive' ? `<strong>Based on the information and statements provided, I acknowledge that my doctor has thoroughly explained the procedure to me, answered all my questions, and I hereby request and consent to the treatment.</strong>` : '';

    return consentText.trim();

  }

  compileInteractiveConsentResult(consent: any, includeAdditional: boolean): string {


    let consentText = `CONSENT FOR ${consent.procedureTitle?.toUpperCase()}\n\n`;

    if (consent.condition) {
      consentText += `${consent.condition} ${consent.conditionText}\n\n`;
    }

    if (consent.treatment) {
      consentText += `${consent.treatment} ${consent.procedureTitle} \n\n`;
    }

    if (consent.generalRisks) {
      consentText += `${consent.generalRisks}\n\n`;
    }

    if (consent.specificRisks) {
      consentText += `${consent.specificRisks} ${consent.specificRisksText}\n\n`;
    }

    if (consent.text) {
      consentText += `${consent.text}\n\n`;
    }

    if (consent.confirmSide && consent.sideOptions?.length && includeAdditional) {
      consentText += `PROCEDURE SIDE: ${consent.selectedProcedureSide}\n`;
      consentText += '\n\n';
    }


    if (consent.confirmImplant && consent.implantOptions?.length && includeAdditional) {
      consentText += `SELECTED IMPLANT: ${consent.selectedImplant}\n`;
      consentText += '\n\n';
    }
   
   
    if (consent.confirmTransfusion && consent.transfusionOptions?.length && includeAdditional) {
      consentText += `BLOOD TRANSFUSION: ${consent.selectedTransfusion}\n`;
      consentText += '\n\n';
    }


    if (consent.additionalRequiredConsent?.length && includeAdditional) {
      const additionalRequiredConsent = consent.additionalRequiredConsent;
      consentText += `REQUIRED CONSENT\nI Consent to the following:\n`;
      for (let item of additionalRequiredConsent) {
        consentText += `${item === consent.selectedRequiredConsent ? '[x]' : '[ ]'} ${item}\n`;
      }
      consentText += '\n\n';
    }

    if (consent.conditionalConsent?.length && includeAdditional) {
      const conditionalConsent = consent.conditionalConsent;
      consentText += `OPTIONAL CONSENT\nI Consent to the following:\n`;
      for (let item of conditionalConsent) {
        consentText += `${item.consentGiven ? '[x]' : '[ ]'} ${item.name}\n`;
      }
      consentText += '\n\n';
    }

    if (includeAdditional) {
      consentText += `Based on the information and statements provided, I acknowledge that my doctor has thoroughly explained the procedure to me, answered all my questions, and I hereby request and consent to the treatment.`;
    }


    return consentText.trim();
  }


  compileStandardConsentResult(consent: any, includeAdditional: boolean): string {
    let consentText = ``;

    if (consent.text) {
      consentText += `${consent.text}\n\n`;
    }

    if (consent.additionalRequiredConsent?.length && includeAdditional) {
      const additionalRequiredConsent = consent.additionalRequiredConsent;
      consentText += `REQUIRED CONSENT\nI Consent to the following:\n`;
      for (let item of additionalRequiredConsent) {
        consentText += `${item === consent.selectedRequiredConsent ? '[x]' : '[ ]'} ${item}\n`;
      }
      consentText += '\n\n';
    }

    if (consent.conditionalConsent?.length && includeAdditional) {
      const conditionalConsent = consent.conditionalConsent;
      consentText += `OPTIONAL CONSENT\nI Consent to the following:\n`;
      for (let item of conditionalConsent) {
        consentText += `${item.consentGiven ? '[x]' : '[ ]'} ${item.name}\n`;
      }
      consentText += '\n\n';
    }

    return consentText.trim();
  }

  /**
   * Generate a PDF for a consent form with practice letterhead
   * @param consentData - The consent form data (from FormGroup value or consent record)
   * @param patient - Patient information object
   * @param options - Optional parameters for customization
   */
  async generateConsentPDF(
    consentData: any,
    patient: any,
    options?: {
      includeSignature?: boolean;
      signatureUrl?: string;
      signatoryName?: string;
      date?: string;
    }
  ): Promise<void> {
    // Build the consent content for PDF
    let pdfContent = '';
    
    // Title
    pdfContent += `${consentData.consentLabel || consentData.form?.label || 'Consent Form'}\n\n`;
    
    // Main consent text (convert HTML to plain text)
    const consentText = consentData.consentText || consentData.form?.text || '';
    const plainText = this.pdfDownloadService.convertToPlainText(consentText);
    pdfContent += `${plainText}\n\n`;
    
    // Interactive consent sections. A patient consent's text is already compiled with these
    // sections and their values, so they are only printed from the template when all we have is
    // the template's own text. Each template field starts with its own heading.
    const formData = consentData.form || consentData;
    const isCompiledConsent = !!consentData.consentText && consentData.consentText !== formData?.text;
    if (formData?.consentType === 'interactive' && !isCompiledConsent) {
      for (const section of [formData.condition, formData.treatment, formData.generalRisks, formData.specificRisks]) {
        if (section) {
          pdfContent += `${section}\n\n`;
        }
      }
    }
    
    // Procedure Side
    if (consentData.confirmSide && consentData.sideOptions?.length) {
      pdfContent += `PROCEDURE SIDE\n`;
      pdfContent += `Please select one:\n`;
      consentData.sideOptions.forEach((option: string) => {
        pdfContent += `[ ] ${option}\n`;
      });
      pdfContent += `\n`;
    }
    
    // Implant
    if (consentData.confirmImplant && consentData.implantOptions?.length) {
      pdfContent += `IMPLANT\n`;
      pdfContent += `Please select one:\n`;
      consentData.implantOptions.forEach((option: string) => {
        pdfContent += `[ ] ${option}\n`;
      });
      pdfContent += `\n`;
    }
    
    // Blood Transfusion
    if (consentData.confirmTransfusion && consentData.transfusionOptions?.length) {
      pdfContent += `BLOOD TRANSFUSION\n`;
      pdfContent += `Please select one:\n`;
      consentData.transfusionOptions.forEach((option: string) => {
        pdfContent += `[ ] ${option}\n`;
      });
      pdfContent += `\n`;
    }
    
    // Required Consent
    if (consentData.additionalRequiredConsent?.length) {
      pdfContent += `REQUIRED CONSENT\n`;
      pdfContent += `I Consent to the following:\n`;
      consentData.additionalRequiredConsent.forEach((consent: string) => {
        pdfContent += `[ ] ${consent}\n`;
      });
      pdfContent += `\n`;
    }
    
    // Optional/Conditional Consent
    if (consentData.conditionalConsent?.length) {
      pdfContent += `OPTIONAL CONSENT\n`;
      pdfContent += `I Consent to the following:\n`;
      consentData.conditionalConsent.forEach((conditional: any) => {
        pdfContent += `[ ] ${conditional.name || conditional}\n`;
      });
      pdfContent += `\n`;
    }
    
    // Interactive consent statement
    if (formData?.consentType === 'interactive') {
      pdfContent += `Based on the information and statements provided, I acknowledge that my doctor has thoroughly explained the procedure to me, answered all my questions, and I hereby request and consent to the treatment.\n\n`;
    }
    
    // Consent acknowledgment
    pdfContent += `I have read and agree to the terms and conditions outlined above.\n\n`;
    
    // Signature section
    pdfContent += `\n\n\n\n`;
    pdfContent += `Signature: ___________________________\n\n`;
    pdfContent += `Print Name: ___________________________\n\n`;
    pdfContent += `Relationship to Patient: ___________________________\n\n`;
    pdfContent += `Date: ___________________________\n\n`;
    
    // Generate filename
    const consentLabel = (consentData.consentLabel || consentData.form?.label || 'Consent').replace(/[^a-z0-9]/gi, '_');
    const filename = `MedicAI_${patient.name}_${patient.surname}_${consentLabel}_${moment().valueOf()}`;
    
    // Download PDF
    await this.pdfDownloadService.downloadPdf({
      content: pdfContent,
      filename: filename,
      patient: patient,
      images: [],
      attachSignature: options?.includeSignature || false,
      signatureUrl: options?.signatureUrl,
      signatoryName: options?.signatoryName,
      date: options?.date || moment().format('YYYY-MM-DD'),
    });
  }
}
